// REST endpoints for Manage customers — customers, their shipping sites and transport routes.
import {
  TAG_TYPES,
  customerShippingTag,
  customerSitesTag,
  customerTransportTag,
} from '@/lib/api/tags';
import { buildListQueryParams, handledError, processServerError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import {
  CUSTOMER_SEARCH_IN,
  CUSTOMER_SITE_DETAILS_TAB,
  CUSTOMER_SITE_SEARCH_IN,
  CUSTOMER_SITE_SORT_FIELDS,
  CUSTOMER_SORT_FIELDS,
} from '../constants/customers.constants';
import {
  buildCustomerDto,
  buildCustomerSitesDto,
  buildCustomerSiteUpdateDto,
  buildTransportationRouteDto,
  findCustomerSiteForm,
  processCustomerDetail,
  processCustomerSitesPage,
  processCustomersPage,
  processTransportationRoutes,
} from '../mappers/customersMappers';

const PATHS = {
  list: '/customers',
  customer: (id) => `/customers/${id}`,
  sites: (customerId) => `/customers/${customerId}/shipping-details`,
  site: (siteId) => `/shipping-details/${siteId}`,
  siteUpdate: (siteId, customerId) => `/shipping-details/${siteId}/account/${customerId}`,
  transportation: (customerId) => `/customers/${customerId}/transportation`,
  transportationRoute: (customerId, id) => `/customers/${customerId}/transportation/${id}`,
  transportationDelete: (id) => `/customers/transportation/${id}`,
};

const CUSTOMER_RELATION = { relationType: 'customer' };

const listTag = { type: TAG_TYPES.Customer, id: 'LIST' };
const customerTag = (id) => ({ type: TAG_TYPES.Customer, id: String(id) });

// A site change moves the list's site count and every dropdown reading the customer's sites.
const siteChangeTags = (customerId) => [
  customerSitesTag(customerId),
  customerShippingTag(customerId),
  customerTransportTag(customerId),
  listTag,
];

export const customersEndpoints = (build) => ({
  getCustomersList: build.query({
    query: (params) => ({
      url: PATHS.list,
      params: buildListQueryParams(params, {
        sortFields: CUSTOMER_SORT_FIELDS,
        searchIn: CUSTOMER_SEARCH_IN,
      }),
    }),
    transformResponse: processCustomersPage,
    providesTags: (result) =>
      result ? [...result.rows.map((row) => customerTag(row.id)), listTag] : [listTag],
  }),

  getCustomer: build.query({
    query: (id) => ({ url: PATHS.customer(id) }),
    transformResponse: (dto) => processCustomerDetail(dto?.output?.data),
    transformErrorResponse: handledError,
    providesTags: (result, error, id) => [customerTag(id)],
  }),

  createCustomer: build.mutation({
    query: (values) => ({ url: PATHS.list, method: 'POST', body: buildCustomerDto(values) }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMERS_ADDED'),
    invalidatesTags: [listTag],
  }),

  updateCustomer: build.mutation({
    query: ({ id, values }) => ({
      url: PATHS.customer(id),
      method: 'PUT',
      body: buildCustomerDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMERS_UPDATED'),
    invalidatesTags: (result, error, arg) => [customerTag(arg.id), listTag],
  }),

  // TODO: wire real API — the backend registers no DELETE /customers/:id
  deleteCustomer: build.mutation({
    query: (id) => ({ url: PATHS.customer(id), method: 'DELETE' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMERS_DELETED'),
    invalidatesTags: [listTag],
  }),

  getCustomerSites: build.query({
    query: ({ customerId, params }) => ({
      url: PATHS.customer(customerId),
      params: {
        tab: CUSTOMER_SITE_DETAILS_TAB,
        ...buildListQueryParams(params, {
          sortFields: CUSTOMER_SITE_SORT_FIELDS,
          searchIn: CUSTOMER_SITE_SEARCH_IN,
        }),
      },
    }),
    transformResponse: processCustomerSitesPage,
    providesTags: (result, error, arg) => [customerSitesTag(arg.customerId)],
  }),

  // Unpaginated, full rows — the edit page picks its one site out of it.
  getCustomerSiteForm: build.query({
    query: ({ customerId }) => ({ url: PATHS.sites(customerId) }),
    transformResponse: (dto, meta, arg) => findCustomerSiteForm(dto, arg.siteId),
    transformErrorResponse: handledError,
    providesTags: (result, error, arg) => [customerSitesTag(arg.customerId)],
  }),

  createCustomerSites: build.mutation({
    query: ({ customerId, values }) => ({
      url: PATHS.sites(customerId),
      method: 'POST',
      body: buildCustomerSitesDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMER_SITES_ADDED'),
    invalidatesTags: (result, error, arg) => siteChangeTags(arg.customerId),
  }),

  // TODO: wire real API — confirm the generic single-site PUT saves every customer-site field
  updateCustomerSite: build.mutation({
    query: ({ customerId, siteId, values }) => ({
      url: PATHS.siteUpdate(siteId, customerId),
      method: 'PUT',
      params: CUSTOMER_RELATION,
      body: buildCustomerSiteUpdateDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMER_SITES_UPDATED'),
    invalidatesTags: (result, error, arg) => siteChangeTags(arg.customerId),
  }),

  deleteCustomerSite: build.mutation({
    query: ({ siteId }) => ({
      url: PATHS.site(siteId),
      method: 'DELETE',
      params: CUSTOMER_RELATION,
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMER_SITES_DELETED'),
    invalidatesTags: (result, error, arg) => siteChangeTags(arg.customerId),
  }),

  getCustomerTransportation: build.query({
    query: (customerId) => ({ url: PATHS.transportation(customerId) }),
    transformResponse: processTransportationRoutes,
    providesTags: (result, error, customerId) => [customerTransportTag(customerId)],
  }),

  createCustomerTransportation: build.mutation({
    query: ({ customerId, values }) => ({
      url: PATHS.transportation(customerId),
      method: 'POST',
      body: buildTransportationRouteDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMER_TRANSPORT_ADDED'),
    invalidatesTags: (result, error, arg) => [customerTransportTag(arg.customerId)],
  }),

  updateCustomerTransportation: build.mutation({
    query: ({ customerId, id, values }) => ({
      url: PATHS.transportationRoute(customerId, id),
      method: 'PUT',
      body: buildTransportationRouteDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMER_TRANSPORT_UPDATED'),
    invalidatesTags: (result, error, arg) => [customerTransportTag(arg.customerId)],
  }),

  deleteCustomerTransportation: build.mutation({
    query: ({ id }) => ({ url: PATHS.transportationDelete(id), method: 'DELETE' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('CUSTOMER_TRANSPORT_DELETED'),
    invalidatesTags: (result, error, arg) => [customerTransportTag(arg.customerId)],
  }),
});
